import type { StaticImageData } from "next/image";
import Faq from "~/components/Faq/Faq";
import type { FaqContent } from "~/components/Faq/FaqContent";
import { Banner } from "~/components/Banner";
import ServiceSelector from "~/components/ServiceSelector";
import { RecentPostsSection } from "~/components/blog/RecentPostsSection";
import { MarginLayout } from "~/components/layouts/MarginLayout";
import { ServiceCitySection } from "~/components/ServiceCitySection";
import SubscriptionServiceBanner from "~/components/SubscriptionServiceBanner";
import { ServiceSection } from "~/components/shared/ServiceSection";
import type { StandardServicePageContent } from "~/content/servicePage";
import type { City } from "~/lib/cities";
import type { PostMeta } from "~/lib/mdx";
import { getServiceFaqs } from "~/lib/serviceFaqs";
import { getServiceBySlug } from "~/lib/services";

type SectionImage = {
  src: StaticImageData | string;
  alt: string;
  imagePosition?: "left" | "right";
};

type StandardServicePageProps = {
  serviceSlug: string;
  content: StandardServicePageContent;
  sectionImages: readonly SectionImage[];
  recentPosts: PostMeta[];
  availableCities?: City[];
};

export function StandardServicePage({
  serviceSlug,
  content,
  sectionImages,
  recentPosts,
  availableCities = [],
}: StandardServicePageProps) {
  const service = getServiceBySlug(serviceSlug);

  if (!service) {
    throw new Error(`Service not found: ${serviceSlug}`);
  }

  const baseUrl =
    (process.env.NEXT_PUBLIC_BASE_URL ?? "https://inspektomat.pl").replace(
      /\/+$/,
      ""
    );
  const pageUrl = `${baseUrl}${service.href}`;
  const serviceFaqs = getServiceFaqs(serviceSlug);
  const faqSections: FaqContent[] = [
    {
      title: content.faqTitle,
      items: serviceFaqs.map((faq, index) => ({
        id: index + 1,
        question: faq.q,
        answer: faq.a,
      })),
    },
  ];
  const faqSelectedIds = faqSections.flatMap((section) =>
    section.items.map((item) => item.id)
  );
  const areaServed = [
    { "@type": "Country", name: "Polska" },
    ...availableCities.slice(0, 12).map((city) => ({
      "@type": "City",
      name: city.name,
    })),
  ];
  const additionalProperty = [
    service.frequency
      ? {
          "@type": "PropertyValue",
          name: "Częstotliwość kontroli",
          value: service.frequency,
        }
      : null,
    service.legalBasis
      ? {
          "@type": "PropertyValue",
          name: "Podstawa prawna",
          value: service.legalBasis,
        }
      : null,
  ].filter(Boolean);
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${pageUrl}#service`,
        name: service.name,
        serviceType: service.shortName,
        description: service.longDescription ?? service.description,
        url: pageUrl,
        category: service.category,
        areaServed,
        provider: {
          "@id": `${baseUrl}/#organization`,
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": pageUrl,
        },
        additionalProperty,
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Strona główna",
            item: `${baseUrl}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: service.name,
            item: pageUrl,
          },
        ],
      },
      ...(serviceFaqs.length > 0
        ? [
            {
              "@type": "FAQPage",
              "@id": `${pageUrl}#faq`,
              mainEntity: serviceFaqs.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.a,
                },
              })),
            },
          ]
        : []),
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
      />

      <Banner title={content.heroTitle} />
      <MarginLayout className="mx-auto max-w-7xl">
        <div className="rounded bg-white shadow-sm flex flex-col gap-3 p-3 md:flex-row md:gap-8 md:p-8">
          <div className="w-full md:w-1/2">
            <ServiceSelector mode="service" initialServiceSlug={serviceSlug} />
          </div>
          <div className="text-gray-700 md:w-1/2 md:flex md:flex-col">
            <h2 className="mb-4 text-xl font-bold text-[#1B3A5C] h3">
              {content.sections[0].title}
            </h2>
            {content.sections[0].paragraphs.map((paragraph, i) => (
              <p key={i} className="mb-3">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
        <div className="mt-20">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            {content.quickFactsTitle}
          </h3>
          <div className="flex gap-3 overflow-x-auto pb-2 pr-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {content.quickFacts.map((fact) => (
              <article
                key={fact.label}
                className="min-w-[220px] flex-1 rounded-2xl border border-sky-100 bg-sky-50/80 p-4"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                  {fact.label}
                </p>
                <p className="mt-2 text-base font-semibold text-gray-900">
                  {fact.value}
                </p>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  {fact.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </MarginLayout>

      {content.sections.slice(1).map((section, index) => {
        const image = sectionImages[index % sectionImages.length];

        return (
          <ServiceSection
            key={section.title}
            title={section.title}
            paragraphs={section.paragraphs}
            image={{
              src: image.src,
              alt: image.alt,
            }}
            imagePosition={image.imagePosition ?? (index % 2 === 0 ? "left" : "right")}
          />
        );
      })}

      {service.cityPages ? (
        <ServiceCitySection
          serviceSlug={serviceSlug}
          serviceNameGenitive={service.nameGenitive}
          cities={availableCities}
        />
      ) : null}

      {content.scopeItems && content.scopeItems.length > 0 ? (
        <MarginLayout className="mx-auto max-w-7xl">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            {content.scopeTitle ?? `Zakres usług ${service.nameGenitive}`}
          </h3>
          <div className="flex flex-wrap gap-2">
            {content.scopeItems.map((item) => (
              <span
                key={item}
                className="rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-xs text-sky-700"
              >
                {item}
              </span>
            ))}
          </div>
        </MarginLayout>
      ) : null}

      {serviceFaqs.length > 0 ? (
        <Faq
          sections={faqSections}
          selectedIds={faqSelectedIds}
        />
      ) : null}
      <SubscriptionServiceBanner />
      <RecentPostsSection posts={recentPosts} />
    </>
  );
}
